import React, { useCallback, memo } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useExternalErrors } from '@alycecom/hooks';

import ConfirmStepButton from '../../../../../components/Shared/ConfirmStepButton';
import { getGiftProduct } from '../../../store/data/data.selectors';
import { getErrors, getIsAnswerSending, getIsLoading } from '../../../store/finishInfo/finishInfo.selectors';
import { TFinishInfoForm, validationSchema } from '../../../store/finishInfo/finishInfo.schemas';

export interface INeedMoreInfoFormProps {
  onSubmit: (message: string) => void;
}

const NeedMoreInfoForm = ({ onSubmit }: INeedMoreInfoFormProps) => {
  const product = useSelector(getGiftProduct);
  const externalErrors = useSelector(getErrors);
  const isLoading = useSelector(getIsLoading);
  const isAnswerSending = useSelector(getIsAnswerSending);

  const isDisabled = isLoading || isAnswerSending;

  const handleSubmitForm = useCallback(
    ({ message }) => {
      onSubmit(message);
    },
    [onSubmit],
  );

  const { errors, handleSubmit, setError, register } = useForm<TFinishInfoForm>({
    mode: 'all',
    defaultValues: {
      message: '',
    },
    resolver: yupResolver(validationSchema),
  });

  useExternalErrors<TFinishInfoForm>(setError, externalErrors);

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      <Box
        height={1}
        width={1}
        display="flex"
        flexDirection="column"
        justifyContent={['space-between', 'flex-start']}
        alignItems="center"
      >
        <Box width={[320, 430]} mt={3} mb={[12, 0]}>
          <Box mb={1} color="text.primary">
            <Typography className="H4-Dark-Bold">Fill out the form to complete your gift process</Typography>
          </Box>
          <Box mb={3} color="text.primary">
            <Typography className="Body-Regular-Left-Static">
              Tell us which option you’d like for your {product.name}
            </Typography>
          </Box>
          <TextField
            name="message"
            placeholder="Type options here"
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            inputRef={register}
            disabled={isDisabled}
            error={!!errors.message}
            helperText={errors.message && errors.message.message}
          />
        </Box>
        <ConfirmStepButton
          type="submit"
          width={[1, 430]}
          mx={[0, 'auto']}
          mb={[0, 2]}
          px={[3, 0]}
          pb={2}
          disabled={isDisabled}
        />
      </Box>
    </form>
  );
};

export default memo(NeedMoreInfoForm);
